import { filterPropertyTypes } from 'boot/globals';

/**
 * Arranging the criteria list: which criteria are shown, how they are grouped, and what each row
 * says about the filter already on it.
 */

/**
 * Below this many criteria the groups flatten into one scroll with section headers. Above it the
 * group level stays, because on the larger tables the criteria are derived from columns and run to
 * several dozen, where flattening turns a two-tap navigation into a long scroll that depends
 * entirely on search.
 */
export const FLATTEN_THRESHOLD = 15;

const byOrder = (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);

const matches = (criterion, term) => String(criterion.label ?? '').toLowerCase().includes(term);

/**
 * Whether the list flattens its groups into a single scroll.
 *
 * @param {object[]} criteria
 * @returns {boolean}
 */
export function shouldFlattenGroups(criteria = []) {
  return criteria.length <= FLATTEN_THRESHOLD;
}

/**
 * The groups present in a set of criteria, in the order the caller declared them, and only those
 * the caller supplied a label for.
 *
 * @param {object[]} criteria
 * @param {{ group: string, label: string }[]} filterGroups
 */
export function getAvailableGroups(criteria = [], filterGroups = []) {
  const present = new Set(criteria.map((criterion) => criterion.group).filter(Boolean));

  return filterGroups.filter((group) => present.has(group.group));
}

/**
 * A criterion cannot be filtered on if it says so, or if it is a list type whose options never
 * arrived: an option list with nothing in it has nothing to choose.
 *
 * @param {object} criterion
 * @returns {boolean}
 */
export function isCriterionDisabled(criterion) {
  if (criterion?.options?.disableItem) {
    return true;
  }

  const isListType = [
    filterPropertyTypes.StringInArray,
    filterPropertyTypes.ArrayInArray,
  ].includes(criterion?.filterType);

  return isListType && (criterion?.options?.filterCriteriaItems ?? []).length === 0;
}

/**
 * How many values a criterion is currently filtering by, or 0 when it is not filtered.
 *
 * A multi-select reports how many it has; anything else reports 1, since "filtered" is the whole
 * story for a date range or a comparison.
 *
 * @param {object} [filter] The applied filter for this criterion, if any
 * @returns {number}
 */
export function getSelectionCount(filter) {
  const statements = filter?.filterStatements ?? [];

  return statements.reduce((total, statement) => total
    + (Array.isArray(statement?.filteredItems) ? statement.filteredItems.length : 1), 0);
}

/**
 * How many of a group's criteria are currently filtered.
 *
 * Counted in criteria rather than in values, so the badge reads against the caption beside it:
 * three of these sixteen filters are doing something. Summing values would let the badge run past
 * the caption's own number.
 *
 * @param {object[]} criteria
 * @param {object} filters The applied filters, keyed by criterion value
 * @param {string} group
 * @returns {number}
 */
export function getGroupFilterCount(criteria, filters, group) {
  return (criteria ?? []).filter((criterion) => criterion.group === group
    && getSelectionCount(filters?.[criterion.value]) > 0).length;
}

/**
 * Label for a row. While searching, a criterion is shown with its group in front, because the
 * results are drawn from every group at once and "Name" alone does not say which one.
 *
 * @param {object} criterion
 * @param {{ group: string, label: string }[]} filterGroups
 * @param {boolean} isSearching
 */
export function getCriterionLabel(criterion, filterGroups = [], isSearching = false) {
  if (!isSearching || !criterion.group) {
    return criterion.label;
  }

  const groupLabel = filterGroups.find((group) => group.group === criterion.group)?.label;

  return groupLabel ? `${groupLabel}: ${criterion.label}` : criterion.label;
}

/**
 * The sections to render, given what the user has typed and which group they have opened.
 *
 * Returns a list of `{ group, label, criteria }`. A section with a null label is the ungrouped
 * block, which sits unheadered at the top.
 *
 * @param {object[]} criteria
 * @param {object} options
 * @param {{ group: string, label: string }[]} [options.filterGroups]
 * @param {string} [options.search]
 * @param {string|null} [options.selectedGroup]
 * @returns {{ group: string|null, label: string|null, criteria: object[] }[]}
 */
export function buildCriteriaSections(criteria = [], options = {}) {
  const { filterGroups = [], search = '', selectedGroup = null } = options;
  const term = search.trim().toLowerCase();
  const sorted = [...criteria].sort(byOrder);

  // Search cuts across groups, so it answers with one flat list rather than a shape to navigate.
  if (term !== '') {
    return [{
      group: null,
      label: null,
      criteria: sorted.filter((criterion) => matches(criterion, term)),
    }];
  }

  if (selectedGroup !== null) {
    return [{
      group: selectedGroup,
      label: filterGroups.find((group) => group.group === selectedGroup)?.label ?? null,
      criteria: sorted.filter((criterion) => criterion.group === selectedGroup),
    }];
  }

  const groups = getAvailableGroups(sorted, filterGroups);

  /*
   * A criterion whose group the caller never declared is ungrouped, not hidden. The session
   * activities table names a group per note template ("formData.<templateId>") while declaring
   * only the "formData" parent, and there is no group row to open for those, so keying strictly on
   * criterion.group would leave every template field unreachable in the list and in the groups.
   */
  const declared = new Set(filterGroups.map((group) => group.group));
  const ungrouped = sorted.filter((criterion) => !criterion.group || !declared.has(criterion.group));

  // Above the threshold the groups stay as rows to open, so only the ungrouped block is listed.
  if (!shouldFlattenGroups(sorted)) {
    return ungrouped.length > 0 ? [{ group: null, label: null, criteria: ungrouped }] : [];
  }

  return [
    ...(ungrouped.length > 0 ? [{ group: null, label: null, criteria: ungrouped }] : []),
    ...groups.map((group) => ({
      group: group.group,
      label: group.label,
      criteria: sorted.filter((criterion) => criterion.group === group.group),
    })),
  ];
}
